import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Order } from '@chicare/shared';

import { api } from '@/lib/api';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';

/** Every order this buyer has placed, newest first. */
export function OrdersScreen() {
  const { data: orders, isLoading, isError, refetch } = useQuery({
    queryKey: ['orders'],
    queryFn: ({ signal }) => api.orders(signal),
  });

  return (
    <>
      <AppBar title="Your orders" />

      <div className="mx-auto w-full max-w-[900px] px-4 pb-6 xl:px-6">
        {isError ? (
          <ErrorState
            title="We couldn't load your orders"
            body="Your orders are safe. This is a connection problem."
            action={
              <Button full icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : isLoading ? (
          <div className="space-y-3 pt-3.5">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-28 w-full" radius="card" />
            ))}
          </div>
        ) : orders?.length === 0 ? (
          <EmptyState
            title="No orders yet"
            body="When you order, you'll be able to follow it here from the pharmacy to your door."
            action={
              <Link
                to="/search"
                className="flex min-h-11 w-full items-center justify-center rounded-btn bg-blue px-4 text-[13.5px] font-bold text-white no-underline"
              >
                Find a medicine
              </Link>
            }
          />
        ) : (
          <div className="space-y-3 pt-3.5">
            {orders?.map((order) => (
              <OrderRow key={order.reference} order={order} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function OrderRow({ order }: { order: Order }) {
  const pharmacies = order.sub_orders?.map((s) => s.pharmacy?.name).filter(Boolean) ?? [];

  return (
    <Link to={`/orders/${order.reference}`} className="block no-underline">
      <Card className="transition-colors hover:border-blue">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <b className="font-mono text-[12.5px]">{order.reference}</b>
              {order.delivery_count && order.delivery_count > 1 ? (
                <Badge>{order.delivery_count} deliveries</Badge>
              ) : null}
            </div>

            <p className="m-0 mt-1 truncate text-[11.5px] text-muted">
              {pharmacies.join(' · ') || '—'}
            </p>

            {order.placed_at ? (
              <p className="m-0 mt-0.5 font-mono text-[10.5px] text-faint">
                {new Date(order.placed_at).toLocaleDateString('en-NG', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            ) : null}
          </div>

          <div className="shrink-0 text-right">
            <span className="block font-display text-[16px] font-bold tracking-[-0.02em]">
              {order.grand_total_display}
            </span>
            <Icon name="chevron" size={14} strokeWidth={2.4} className="ml-auto mt-1 text-faint" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
